/**
 * Estado dos filtros de cada aba dos relatórios + a lista de "filtros
 * ativos" que alimenta, ao mesmo tempo, os chips (com ✕) e o rótulo do
 * relatório no CSV/PDF. Uma fonte só: o que o chip mostra é o que o
 * arquivo exportado diz.
 */
import { ETAPA_FECHADO } from '@/features/orcamentos/api/etapasKanban';
import {
  ROTULO_BASE_DATA,
  ROTULO_CADASTRO,
  ROTULO_ORIGEM,
  ROTULO_PRAZO,
  ROTULO_SITUACAO_ESTOQUE,
  SEM_COLABORADOR,
  SEM_RESPONSAVEL,
  STATUS_EXCETO_CANCELADAS,
  STATUS_TODOS,
  rotuloStatusOS,
  type BaseData,
  type CadastroProdutoFiltro,
  type FiltroPrazo,
  type FiltroStatusOS,
  type FiltroTipoItem,
  type OrigemMovimentoFiltro,
  type SituacaoEstoqueFiltro,
  type TipoMovimentoEstoqueFiltro,
} from '@/features/relatorios/filtrosRelatorio';

/** Um filtro aplicado, como aparece no chip e no rótulo do relatório. */
export type FiltroAtivo = {
  id: string;
  rotulo: string;
  valor: string;
  limpar: () => void;
  /**
   * true = é o valor padrão da aba. Entra no rótulo do relatório (ajuda a
   * ler o número — ex.: "Status: Todas exceto canceladas"), mas não vira
   * chip, porque não há o que limpar.
   */
  padrao?: boolean;
};

/** "Período: 01/09/2026 a 30/09/2026 • Cliente: Fulano • Status: …" */
export function rotuloFiltrosAtivos(filtros: FiltroAtivo[]): string {
  return filtros.map((f) => `${f.rotulo}: ${f.valor}`).join(' • ');
}

/** Só os que valem chip (diferentes do padrão). */
export function chipsDeFiltros(filtros: FiltroAtivo[]): FiltroAtivo[] {
  return filtros.filter((f) => !f.padrao);
}

/** `YYYY-MM-DD` → `DD/MM/YYYY` sem passar por `Date` (nada de fuso). */
export function formatarDiaIso(dia: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dia);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dia;
}

// ---------------------------------------------------------------------------
// Estado por aba
// ---------------------------------------------------------------------------

export type VisaoItens = 'detalhado' | 'ranking';

export type FiltrosAbaVisao = { baseData: BaseData };

export type FiltrosAbaOrcamentos = {
  status: FiltroStatusOS;
  responsavel: string;
  prazo: FiltroPrazo;
  baseData: BaseData;
  busca: string;
};

/** Peças e Serviços (cada aba tem o seu, com o mesmo formato). */
export type FiltrosAbaItens = {
  status: FiltroStatusOS;
  referenciaId: string;
  colaborador: string;
  somenteComDesconto: boolean;
  visao: VisaoItens;
  busca: string;
};

export type FiltrosAbaColaboradores = {
  status: FiltroStatusOS;
  colaborador: string;
  tipo: FiltroTipoItem;
  baseData: BaseData;
  busca: string;
};

export type FiltrosAbaEstoque = {
  situacao: SituacaoEstoqueFiltro;
  cadastro: CadastroProdutoFiltro;
  tipoMovimento: TipoMovimentoEstoqueFiltro;
  origem: OrigemMovimentoFiltro;
  ocultarEstornadas: boolean;
  busca: string;
};

export type FiltrosAbaVeiculo = { ocultarCanceladas: boolean };

export const PADRAO_VISAO: FiltrosAbaVisao = { baseData: 'abertura' };

export const PADRAO_ORCAMENTOS: FiltrosAbaOrcamentos = {
  status: STATUS_TODOS,
  responsavel: '',
  prazo: '',
  baseData: 'abertura',
  busca: '',
};

export const PADRAO_ITENS: FiltrosAbaItens = {
  status: STATUS_EXCETO_CANCELADAS,
  referenciaId: '',
  colaborador: '',
  somenteComDesconto: false,
  visao: 'detalhado',
  busca: '',
};

export const PADRAO_COLABORADORES: FiltrosAbaColaboradores = {
  status: STATUS_EXCETO_CANCELADAS,
  colaborador: '',
  tipo: '',
  baseData: 'abertura',
  busca: '',
};

export const PADRAO_ESTOQUE: FiltrosAbaEstoque = {
  situacao: '',
  cadastro: 'ativos',
  tipoMovimento: '',
  origem: '',
  ocultarEstornadas: false,
  busca: '',
};

export const PADRAO_VEICULO: FiltrosAbaVeiculo = { ocultarCanceladas: false };

// ---------------------------------------------------------------------------
// Montagem da lista
// ---------------------------------------------------------------------------

type Definicao<T> = {
  campo: keyof T & string;
  rotulo: string;
  valor: (v: T[keyof T]) => string;
  /** Mostra no rótulo mesmo quando está no padrão. */
  sempre?: boolean;
};

/**
 * Um item por campo que difere do padrão (limpar = volta ao padrão).
 * Campos `sempre` entram também quando estão no padrão, marcados `padrao`.
 */
export function montarFiltrosAtivos<T extends object>(
  estado: T,
  padrao: T,
  aplicar: (parcial: Partial<T>) => void,
  definicoes: Array<Definicao<T>>,
): FiltroAtivo[] {
  const lista: FiltroAtivo[] = [];
  for (const d of definicoes) {
    const v = estado[d.campo];
    const ehPadrao = v === padrao[d.campo];
    if (ehPadrao && !d.sempre) continue;
    lista.push({
      id: d.campo,
      rotulo: d.rotulo,
      valor: d.valor(v),
      limpar: () => aplicar({ [d.campo]: padrao[d.campo] } as Partial<T>),
      ...(ehPadrao ? { padrao: true } : {}),
    });
  }
  return lista;
}

type Contexto = {
  rotuloDe: (id: string) => string;
  /** Nome de um colaborador/responsável pelo id. */
  nomeColaborador: (id: string) => string;
  /** Nome de uma peça/serviço pelo id. */
  nomeItem?: (id: string) => string;
};

const busca = (v: unknown) => `"${String(v)}"`;

export function filtrosAtivosVisao(
  f: FiltrosAbaVisao,
  aplicar: (p: Partial<FiltrosAbaVisao>) => void,
): FiltroAtivo[] {
  return montarFiltrosAtivos(f, PADRAO_VISAO, aplicar, [
    { campo: 'baseData', rotulo: 'Data base', valor: (v) => ROTULO_BASE_DATA[v as BaseData] },
  ]);
}

export function filtrosAtivosOrcamentos(
  f: FiltrosAbaOrcamentos,
  aplicar: (p: Partial<FiltrosAbaOrcamentos>) => void,
  ctx: Contexto,
): FiltroAtivo[] {
  return montarFiltrosAtivos(f, PADRAO_ORCAMENTOS, aplicar, [
    { campo: 'baseData', rotulo: 'Data base', valor: (v) => ROTULO_BASE_DATA[v as BaseData] },
    { campo: 'status', rotulo: 'Status', valor: (v) => rotuloStatusOS(String(v), ctx.rotuloDe) },
    {
      campo: 'responsavel',
      rotulo: 'Responsável',
      valor: (v) => (v === SEM_RESPONSAVEL ? 'Sem responsável' : ctx.nomeColaborador(String(v))),
    },
    {
      campo: 'prazo',
      rotulo: 'Prazo',
      valor: (v) => ROTULO_PRAZO[v as Exclude<FiltroPrazo, ''>] ?? String(v),
    },
    { campo: 'busca', rotulo: 'Busca', valor: busca },
  ]);
}

export function filtrosAtivosItens(
  f: FiltrosAbaItens,
  aplicar: (p: Partial<FiltrosAbaItens>) => void,
  ctx: Contexto & { rotuloItem: string },
): FiltroAtivo[] {
  return montarFiltrosAtivos(f, PADRAO_ITENS, aplicar, [
    {
      campo: 'status',
      rotulo: 'Status da O.S.',
      valor: (v) => rotuloStatusOS(String(v), ctx.rotuloDe),
      sempre: true,
    },
    {
      campo: 'referenciaId',
      rotulo: ctx.rotuloItem,
      valor: (v) => ctx.nomeItem?.(String(v)) ?? String(v),
    },
    {
      campo: 'colaborador',
      rotulo: 'Colaborador',
      valor: (v) => (v === SEM_COLABORADOR ? 'Sem colaborador' : ctx.nomeColaborador(String(v))),
    },
    { campo: 'somenteComDesconto', rotulo: 'Desconto', valor: () => 'Somente com desconto' },
    {
      campo: 'visao',
      rotulo: 'Visão',
      valor: (v) => (v === 'ranking' ? 'Ranking' : 'Detalhado'),
    },
    { campo: 'busca', rotulo: 'Busca', valor: busca },
  ]);
}

export function filtrosAtivosColaboradores(
  f: FiltrosAbaColaboradores,
  aplicar: (p: Partial<FiltrosAbaColaboradores>) => void,
  ctx: Contexto,
): FiltroAtivo[] {
  return montarFiltrosAtivos(f, PADRAO_COLABORADORES, aplicar, [
    { campo: 'baseData', rotulo: 'Data base', valor: (v) => ROTULO_BASE_DATA[v as BaseData] },
    {
      campo: 'status',
      rotulo: 'Status da O.S.',
      valor: (v) => rotuloStatusOS(String(v), ctx.rotuloDe),
      sempre: true,
    },
    { campo: 'colaborador', rotulo: 'Colaborador', valor: (v) => ctx.nomeColaborador(String(v)) },
    { campo: 'tipo', rotulo: 'Tipo', valor: (v) => (v === 'Produto' ? 'Peças' : 'Serviços') },
    { campo: 'busca', rotulo: 'Busca', valor: busca },
  ]);
}

export function filtrosAtivosEstoque(
  f: FiltrosAbaEstoque,
  aplicar: (p: Partial<FiltrosAbaEstoque>) => void,
): FiltroAtivo[] {
  return montarFiltrosAtivos(f, PADRAO_ESTOQUE, aplicar, [
    {
      campo: 'cadastro',
      rotulo: 'Cadastro',
      valor: (v) => ROTULO_CADASTRO[v as CadastroProdutoFiltro],
      sempre: true,
    },
    {
      campo: 'situacao',
      rotulo: 'Situação',
      valor: (v) => ROTULO_SITUACAO_ESTOQUE[v as Exclude<SituacaoEstoqueFiltro, ''>] ?? String(v),
    },
    {
      campo: 'tipoMovimento',
      rotulo: 'Movimento',
      valor: (v) => (v === 'Entrada' ? 'Entradas' : 'Saídas'),
    },
    {
      campo: 'origem',
      rotulo: 'Origem',
      valor: (v) =>
        ROTULO_ORIGEM[v as Exclude<OrigemMovimentoFiltro, ''>] ?? String(v),
    },
    { campo: 'ocultarEstornadas', rotulo: 'Estornadas', valor: () => 'Ocultas' },
    { campo: 'busca', rotulo: 'Busca', valor: busca },
  ]);
}

export function filtrosAtivosVeiculo(
  f: FiltrosAbaVeiculo,
  aplicar: (p: Partial<FiltrosAbaVeiculo>) => void,
): FiltroAtivo[] {
  return montarFiltrosAtivos(f, PADRAO_VEICULO, aplicar, [
    { campo: 'ocultarCanceladas', rotulo: 'O.S. canceladas', valor: () => 'Ocultas' },
  ]);
}

/** Etapa "fechadas/pagas" re-exportada para quem monta o select de status. */
export const STATUS_SOMENTE_FECHADAS = ETAPA_FECHADO;
